import { LocaleMessageValue } from 'vue-i18n';
import type { Layer as LeafletLayer, PopupEvent } from 'leaflet';

import colors from './colors';

import { CountriesModel } from '@/models/sidebar/info.model';
import { CountryLocales, Feature, Layer, Layout, PopupContent } from '@/models/sidebar/places.model';
import store from '@/store';

let db: CountriesModel[] = [];
let locales: CountryLocales = {} as CountryLocales;
let layout: Layout = 'ltr';
let lang = 'en';

const getLocalizedValue = function(obj: CountriesModel | undefined, keyName: string): string {
  if (!obj) {
    return '';
  }

  if (lang !== 'en' && obj[keyName + '_' + lang]) {
    return String(obj[keyName + '_' + lang]);
  }
  return String(obj[keyName] || '');
};

function adjustColor(color: string, percent: number, isDark: boolean): string {
  if (color === 'transparent') {
    return isDark ? '#121722' : '#ffffff';
  }

  if (color === 'red') {
    color = '#ff0000';
  }
  if (color === 'green') {
    color = '#008000';
  }

  const hex = color.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16) || 0;
  let g = parseInt(hex.substring(2, 4), 16) || 0;
  let b = parseInt(hex.substring(4, 6), 16) || 0;

  if (isDark) {
    r = Math.round(r * (1 - percent));
    g = Math.round(g * (1 - percent));
    b = Math.round(b * (1 - percent));
  }
  else {
    r = Math.round(r + (255 - r) * percent);
    g = Math.round(g + (255 - g) * percent);
    b = Math.round(b + (255 - b) * percent);
  }

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function processCountryPopup(feature: Feature, layer: Layer): void {
  const country = db.find((e) => e._id === feature.properties.id);
  layer.tag = 'geo';

  if (!country) {
    return;
  }

  const popup: PopupContent = { ltr: '', rtl: '' };

  popup.ltr = `
    <img class='popupflag' src='${country?.flag || ''}' alt='404'>
    <b style='font-size:20px;'>${country?.name || ''}</b><br>
    <b>${locales['status'] || 'Status'}</b>: ${country?.status || ''}<br>
    <b>${locales['government'] || 'Government'}</b>: ${country?.government || ''}<br>
    <b>${locales['capital'] || 'Capital'}</b>: ${country?.capital || ''}<br>
    <b>${locales['ruling_party'] || 'Ruling Party'}</b>: ${country?.party || ''}<br>
    <b>${locales['head_of_state'] || 'Head of State'}</b>: ${country?.headOfState || ''}<br>
    <b>${locales['head_of_government'] || 'Head of Government'}</b>: ${country?.headOfGovernment || ''}
  `;

  popup.rtl = `
    <img class='popupflag' src='${country?.flag || ''}' alt='404'>
    <div>
      <b style='font-size:20px;'>${getLocalizedValue(country, 'name')}</b><br>
      <b>${locales['status'] || 'Status'}</b>: ${getLocalizedValue(country, 'status')}<br>
      <b>${locales['government'] || 'Government'}</b>: ${getLocalizedValue(country, 'government')}<br>
      <b>${locales['capital'] || 'Capital'}</b>: ${getLocalizedValue(country, 'capital')}<br>
      <b>${locales['ruling_party'] || 'Ruling Party'}</b>: ${getLocalizedValue(country, 'party')}<br>
      <b>${locales['head_of_state'] || 'Head of State'}</b>: ${getLocalizedValue(country, 'headOfState')}<br>
      <b>${locales['head_of_government'] || 'Head of Government'}</b>: ${getLocalizedValue(country, 'headOfGovernment')}
    </div>
  `;

  const realLayer = layer as unknown as LeafletLayer;

  realLayer.bindPopup(popup[layout], {
    className: 'dynamic-faction-popup'
  });

  const factionKey = feature.properties?.pane || 'neutral';
  const rawColor = colors[factionKey as keyof typeof colors] || colors.neutral;

  realLayer.on('popupopen', (e: PopupEvent) => {
    const popupElement = e.popup.getElement();
    if (popupElement) {
      const currentIsDark = store.state.dark;

      const bgColor = adjustColor(rawColor, 0.85, currentIsDark);
      const textColor = currentIsDark ? '#E6EAF2' : '#333333';

      popupElement.style.setProperty('--faction-bg', bgColor);
      popupElement.style.setProperty('--faction-text', textColor);
    }
  });
}

function cacheEvents(data: CountriesModel[], locale: CountryLocales | LocaleMessageValue, l: string, _layout: Layout): void {
  db = data;
  lang = l;
  layout = _layout;

  if (typeof locale === 'object' && locale !== null && !Array.isArray(locale)) {
    if ('status' in locale && 'government' in locale) {
      locales = locale as CountryLocales;
    }
    else {
      const messageDict = locale;
      locales = {
        status: String(messageDict.status || 'Status'),
        government: String(messageDict.government || 'Government'),
        capital: String(messageDict.capital || 'Capital'),
        ruling_party: String(messageDict.ruling_party || 'Ruling Party'),
        head_of_state: String(messageDict.head_of_state || 'Head of State'),
        head_of_government: String(messageDict.head_of_government || 'Head of Government'),
      };
    }
  }
  else {
    locales = {
      status: 'Status',
      government: 'Government',
      capital: 'Capital',
      ruling_party: 'Ruling Party',
      head_of_state: 'Head of State',
      head_of_government: 'Head of Government',
    };
  }
}

export { processCountryPopup, cacheEvents };
